import { parseAsString, useQueryState } from 'nuqs';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { UseTripsOptions, UseTripsResult } from '@/hooks/useTrips';
import { useTrips } from '@/hooks/useTrips';

interface TripContextValue extends UseTripsResult {
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  /** Open the panel and switch the active trip to tripId. */
  navigatePanelToTrip: (tripId: string) => void;
  selectedTripId: string | null;
  selectTrip: (tripId: string) => void;
  deselectTrip: () => void;
}

const TripContext = createContext<TripContextValue | undefined>(undefined);

export interface TripProviderProps extends PropsWithChildren {
  /** Test-only seam, forwarded to `useTrips` — see `UseTripsOptions`.
   *  Production callers (App.tsx) never pass this, so `useTrips` gets its
   *  real, WebSocket-opening `createTripSync`. Test-only wrappers (e.g.
   *  `MockTripProvider`) pass a fake so component tests never open real
   *  network connections. */
  createTripSync?: UseTripsOptions['createTripSync'];
}

export const TripProvider = ({ children, createTripSync }: TripProviderProps) => {
  const tripsApi = useTrips(createTripSync ? { createTripSync } : undefined);
  const [isPanelOpen, setIsPanelOpen] = useState(() =>
    new URLSearchParams(window.location.search).has('trip'),
  );
  const [selectedTripId, setSelectedTripId] = useQueryState('trip', parseAsString);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const navigatePanelToTrip = useCallback(
    (tripId: string) => {
      void setSelectedTripId(tripId);
      setIsPanelOpen(true);
    },
    [setSelectedTripId],
  );

  const selectTrip = useCallback(
    (tripId: string) => {
      void setSelectedTripId(tripId);
      setIsPanelOpen(true);
    },
    [setSelectedTripId],
  );

  const deselectTrip = useCallback(() => {
    void setSelectedTripId(null);
    setIsPanelOpen(false);
  }, [setSelectedTripId]);

  useEffect(() => {
    if (selectedTripId && !tripsApi.trips.some((t) => t.id === selectedTripId)) {
      void setSelectedTripId(null);
      // Close the slide-over panel too — otherwise its body (a
      // fixed, z-modal, w-screen div containing a now-empty
      // TripPanel) stays mounted and intercepts touches, leaving
      // the underlying scrim un-tappable on mobile. This bit users
      // who deleted the active trip from the trip menu on iOS.
      setIsPanelOpen(false);
    }
  }, [tripsApi.trips, selectedTripId, setSelectedTripId]);

  // Memoize the context value so it only changes identity when one of its
  // dependencies does. `tripsApi` is already memoed inside useTrips, so the
  // whole value is stable across renders unless trips/panel/selection change.
  const value = useMemo<TripContextValue>(
    () => ({
      ...tripsApi,
      isPanelOpen,
      openPanel,
      closePanel,
      navigatePanelToTrip,
      selectedTripId,
      selectTrip,
      deselectTrip,
    }),
    [
      tripsApi,
      isPanelOpen,
      openPanel,
      closePanel,
      navigatePanelToTrip,
      selectedTripId,
      selectTrip,
      deselectTrip,
    ],
  );

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
};

export const useTripContext = (): TripContextValue => {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTripContext must be used within a TripProvider');
  return ctx;
};
